import React from 'react';
import { Building2, CheckCircle, DollarSign, MapPin, BarChart3 } from 'lucide-react';

const PropertyStats = ({ stats }) => {
  // Provide safe defaults for destructuring
  const {
    total = 0,
    byStatus = {},
    byType = {},
    byCommune = {},
    avgPrice = 0,
    priceRange = {}
  } = stats || {};

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'reserved': return 'text-yellow-600 bg-yellow-100';
      case 'sold': return 'text-blue-600 bg-blue-100';
      case 'withdrawn': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const availablePercentage = total > 0 ? ((byStatus?.available || 0) / total * 100) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Properties */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Propriétés</p>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center text-sm">
            <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
            <span className="text-gray-600">
              {byStatus?.available || 0} disponibles ({availablePercentage?.toFixed(1)}%)
            </span>
          </div>
        </div>
      </div>

      {/* Average Price */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Prix Moyen</p>
            <p className="text-2xl font-bold text-gray-900">
              {avgPrice ? `${(avgPrice / 1000000)?.toFixed(1)}M` : '0'}
            </p>
          </div>
          <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="h-6 w-6 text-green-600" />
          </div>
        </div>
        <div className="mt-4">
          <div className="text-sm text-gray-600">
            <div>Min: {priceRange?.min ? `${(priceRange?.min / 1000000)?.toFixed(1)}M` : '0'} FCFA</div>
            <div>Max: {priceRange?.max ? `${(priceRange?.max / 1000000)?.toFixed(1)}M` : '0'} FCFA</div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Répartition Statuts</p>
            <p className="text-2xl font-bold text-gray-900">{Object.keys(byStatus || {})?.length}</p>
          </div>
          <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <BarChart3 className="h-6 w-6 text-yellow-600" />
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(byStatus || {})?.slice(0, 3)?.map(([status, count]) => (
            <div key={status} className="flex items-center justify-between">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                {status}
              </span>
              <span className="text-sm font-medium text-gray-900">{count}</span>
            </div>
          ))}
          {Object.keys(byStatus || {})?.length === 0 && (
            <div className="text-xs text-gray-500 italic">Aucune donnée disponible</div>
          )}
        </div>
      </div>

      {/* Top Communes */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Top Communes</p>
            <p className="text-2xl font-bold text-gray-900">{Object.keys(byCommune || {})?.length}</p>
          </div>
          <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <MapPin className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(byCommune || {})
            ?.sort(([,a], [,b]) => b - a)
            ?.slice(0, 3)
            ?.map(([commune, count]) => (
            <div key={commune} className="flex items-center justify-between">
              <span className="text-xs text-gray-600 truncate">{commune}</span>
              <span className="text-sm font-medium text-gray-900">{count}</span>
            </div>
          ))}
          {Object.keys(byCommune || {})?.length === 0 && (
            <div className="text-xs text-gray-500 italic">Aucune donnée disponible</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyStats;